import {
  arrayRemove,
  collection,
  doc,
  getDoc,
  getDocs,
  query,
  runTransaction,
  serverTimestamp,
  updateDoc,
  where,
  type Firestore,
} from 'firebase/firestore'
import type { CanvasElement, ChecklistItem } from '../types/schema'
import { scopedReadConstraints } from './accessScopeService'
import { rollupInputFor, rollupProgress } from './repeat'

const CANVAS_ELEMENTS_COLLECTION = 'elements'
const CHECKLIST_ITEMS_COLLECTION = 'checklists'

const clampProgress = (value: number) => Math.max(0, Math.min(100, Math.round(value)))

const readCanvasElement = async (db: Firestore, elementId: string): Promise<CanvasElement> => {
  const snapshot = await getDoc(doc(db, CANVAS_ELEMENTS_COLLECTION, elementId))
  if (!snapshot.exists()) {
    throw new Error(`CanvasElement not found: ${elementId}`)
  }

  return { id: snapshot.id, ...snapshot.data() } as CanvasElement
}

const readChecklistItem = async (db: Firestore, itemId: string): Promise<ChecklistItem> => {
  const snapshot = await getDoc(doc(db, CHECKLIST_ITEMS_COLLECTION, itemId))
  if (!snapshot.exists()) {
    throw new Error(`ChecklistItem not found: ${itemId}`)
  }

  return { id: snapshot.id, ...snapshot.data() } as ChecklistItem
}

const readOptionalChecklistItem = async (db: Firestore, itemId: string): Promise<ChecklistItem | null> => {
  const snapshot = await getDoc(doc(db, CHECKLIST_ITEMS_COLLECTION, itemId))
  if (!snapshot.exists()) return null
  return { id: snapshot.id, ...snapshot.data() } as ChecklistItem
}

const getChecklistItemsForContainer = async (
  db: Firestore,
  workspaceId: string,
  parentContainerId: string,
): Promise<ChecklistItem[]> => {
  const snapshot = await getDocs(query(
    collection(db, CHECKLIST_ITEMS_COLLECTION),
    where('workspaceId', '==', workspaceId),
    where('parentContainerId', '==', parentContainerId),
    ...scopedReadConstraints(workspaceId),
  ))

  return snapshot.docs.map((itemDocument) => ({
    id: itemDocument.id,
    ...itemDocument.data(),
  })) as ChecklistItem[]
}

export const calculateChecklistProgress = (
  items: ChecklistItem[] | Array<Partial<ChecklistItem> | Record<string, unknown>>,
) => {
  return rollupProgress(items.map((item) => rollupInputFor(item)))
}

const writeElementProgress = async (db: Firestore, elementId: string, progress: number) => {
  await updateDoc(doc(db, CANVAS_ELEMENTS_COLLECTION, elementId), { progress })
  console.log(`[CascadeEngine] ${elementId} progress -> ${progress}%`)
}

const getDescendantElements = async (
  db: Firestore,
  workspaceId: string,
  parentId: string,
): Promise<CanvasElement[]> => {
  const directChildrenSnapshot = await getDocs(query(
    collection(db, CANVAS_ELEMENTS_COLLECTION),
    where('workspaceId', '==', workspaceId),
    where('parentId', '==', parentId),
    ...scopedReadConstraints(workspaceId),
  ))

  const directChildren = directChildrenSnapshot.docs.map((elementDocument) => ({
    id: elementDocument.id,
    ...elementDocument.data(),
  })) as CanvasElement[]

  const nestedChildren = await Promise.all(
    directChildren.map((child) => getDescendantElements(db, workspaceId, child.id)),
  )

  return [...directChildren, ...nestedChildren.flat()]
}

const getChecklistItemsForElementTree = async (
  db: Firestore,
  element: CanvasElement,
): Promise<ChecklistItem[]> => {
  const descendants = await getDescendantElements(db, element.workspaceId, element.id)
  const containerIds = [element.id, ...descendants.map((descendant) => descendant.id)]
  const checklistGroups = await Promise.all(
    containerIds.map((containerId) => getChecklistItemsForContainer(db, element.workspaceId, containerId)),
  )

  return checklistGroups.flat()
}

const calculateElementTreeChecklistProgress = async (
  db: Firestore,
  element: CanvasElement,
) => calculateChecklistProgress(await getChecklistItemsForElementTree(db, element))

export const recalculateElementProgressTree = async (
  db: Firestore,
  elementId: string,
): Promise<void> => {
  let current: CanvasElement | null = await readCanvasElement(db, elementId)

  while (current) {
    const progress = await calculateElementTreeChecklistProgress(db, current)
    await writeElementProgress(db, current.id, progress)
    current = current.parentId ? await readCanvasElement(db, current.parentId) : null
  }
}

export const evaluateDependencyLockStatus = async (
  db: Firestore,
  item: ChecklistItem,
): Promise<boolean> => {
  if (item.prerequisites.length > 0) {
    const prerequisiteItems = (await Promise.all(
      item.prerequisites.map((prerequisiteId) => readOptionalChecklistItem(db, prerequisiteId)),
    )).filter((prerequisite): prerequisite is ChecklistItem => Boolean(prerequisite))
    const isPrerequisiteLocked = item.dependencyLogicGate === 'ANY_PREREQUISITE_MET'
      ? !prerequisiteItems.some((prerequisite) => prerequisite.isCompleted)
      : prerequisiteItems.some((prerequisite) => !prerequisite.isCompleted)

    if (prerequisiteItems.length > 0 && isPrerequisiteLocked) return true
  }

  const blockerTargetId = item.blockedByExternalId ?? item.blockedByExternal?.targetId ?? null
  if (!blockerTargetId) return false

  if (blockerTargetId === item.parentContainerId) {
    throw new Error('Reflexive Parent-Dependency Loop Defended.')
  }

  const blocker = await readCanvasElement(db, blockerTargetId)
  if (blocker.workspaceId !== item.workspaceId) {
    throw new Error('Cross-workspace dependency reference rejected.')
  }

  return clampProgress(blocker.progress) < 100
}

export const executeChecklistMutation = async (
  db: Firestore,
  itemId: string,
  targetStatus: boolean,
): Promise<void> => {
  const targetItem = await readChecklistItem(db, itemId)
  const isLockedByDependency = await evaluateDependencyLockStatus(db, targetItem)

  if (isLockedByDependency) {
    throw new Error('Checklist item is locked by an unresolved external dependency.')
  }

  console.log(`[CascadeEngine] Toggling ${targetItem.id}: ${targetItem.isCompleted} -> ${targetStatus}`)

  await updateDoc(doc(db, CHECKLIST_ITEMS_COLLECTION, itemId), {
    isCompleted: targetStatus,
  })

  const container = await readCanvasElement(db, targetItem.parentContainerId)
  await recalculateElementProgressTree(db, container.id)
}

export const removeChecklistPrerequisiteDependency = async (
  db: Firestore,
  targetItemId: string,
  prerequisiteItemId: string,
): Promise<void> => {
  const targetItem = await readChecklistItem(db, targetItemId)

  await updateDoc(doc(db, CHECKLIST_ITEMS_COLLECTION, targetItemId), {
    prerequisites: arrayRemove(prerequisiteItemId),
  })

  await recalculateElementProgressTree(db, targetItem.parentContainerId)
}

/**
 * Atomically replaces a checklist node's prerequisites and their reciprocal
 * blocker links. The preflight graph check uses the same prerequisite model
 * as manual cascade validation; the transaction then protects every affected
 * node against concurrent agent/manual edits.
 */
export const updateChecklistPrerequisiteDependencies = async (
  db: Firestore,
  targetItemId: string,
  prerequisiteItemIds: string[],
): Promise<void> => {
  const uniqueIds = Array.from(new Set(prerequisiteItemIds.filter(Boolean)))
  if (!targetItemId || uniqueIds.includes(targetItemId)) {
    throw new Error('A checklist item cannot depend on itself.')
  }

  const target = await readChecklistItem(db, targetItemId)
  const workspaceSnapshot = await getDocs(query(
    collection(db, CHECKLIST_ITEMS_COLLECTION),
    where('workspaceId', '==', target.workspaceId),
    ...scopedReadConstraints(target.workspaceId),
  ))
  const workspaceItems = workspaceSnapshot.docs.map((snapshot) => ({
    id: snapshot.id,
    ...snapshot.data(),
  })) as ChecklistItem[]
  const byId = new Map(workspaceItems.map((item) => [item.id, item]))

  for (const prerequisiteId of uniqueIds) {
    if (!byId.has(prerequisiteId)) throw new Error(`Checklist prerequisite not found: ${prerequisiteId}`)
    const visited = new Set<string>()
    const dependsOnTarget = (candidateId: string): boolean => {
      if (candidateId === targetItemId) return true
      if (visited.has(candidateId)) return false
      visited.add(candidateId)
      return (byId.get(candidateId)?.prerequisites ?? []).some(dependsOnTarget)
    }
    if (dependsOnTarget(prerequisiteId)) {
      throw new Error('This dependency would create a checklist cycle.')
    }
  }

  await runTransaction(db, async (transaction) => {
    const targetRef = doc(db, CHECKLIST_ITEMS_COLLECTION, targetItemId)
    const targetSnapshot = await transaction.get(targetRef)
    if (!targetSnapshot.exists()) throw new Error(`ChecklistItem not found: ${targetItemId}`)
    const currentTarget = { id: targetSnapshot.id, ...targetSnapshot.data() } as ChecklistItem
    const affectedIds = Array.from(new Set([...(currentTarget.prerequisites ?? []), ...uniqueIds]))
    const affectedSnapshots = await Promise.all(affectedIds.map((itemId) =>
      transaction.get(doc(db, CHECKLIST_ITEMS_COLLECTION, itemId)),
    ))
    const affectedItems = new Map<string, ChecklistItem>()
    for (const snapshot of affectedSnapshots) {
      if (!snapshot.exists()) throw new Error(`Checklist prerequisite not found: ${snapshot.id}`)
      const item = { id: snapshot.id, ...snapshot.data() } as ChecklistItem
      if (item.workspaceId !== currentTarget.workspaceId) {
        throw new Error('Cross-workspace dependency reference rejected.')
      }
      affectedItems.set(item.id, item)
    }

    transaction.update(targetRef, {
      prerequisites: uniqueIds,
      blockedByNodeIds: uniqueIds.map((itemId) => ({
        targetId: itemId,
        targetType: 'CHECKLIST' as const,
        title: affectedItems.get(itemId)?.title ?? 'Checklist item',
      })),
      updatedAt: serverTimestamp(),
    })

    for (const [itemId, item] of affectedItems) {
      const otherLinks = (item.isBlockerForNodeIds ?? []).filter((link) => link.targetId !== targetItemId)
      transaction.update(doc(db, CHECKLIST_ITEMS_COLLECTION, itemId), {
        isBlockerForNodeIds: uniqueIds.includes(itemId)
          ? [...otherLinks, { targetId: targetItemId, targetType: 'CHECKLIST', title: currentTarget.title }]
          : otherLinks,
        updatedAt: serverTimestamp(),
      })
    }
  })
}

export const phase1CascadeCollections = {
  canvasElements: CANVAS_ELEMENTS_COLLECTION,
  checklistItems: CHECKLIST_ITEMS_COLLECTION,
} as const
